import type { Block, CalendarValue } from '@kioskforge/shared';
import {
  EMPTY_BINDING,
  attributionText,
  formatLocalDay,
  formatLocalTime,
  loadValue,
  localDayKey,
  type WidgetDataBinding
} from './widgetData';
import { AttributionLine, StaleNotice, WidgetStateNotice } from './widgetState';

export type CalendarWidget = Extract<Block, { type: 'calendar' }>;

/** One event, with the display fields the widget resolves from a normalised value. */
type PlacedEvent = {
  title: string;
  start: string;
  end?: string;
  location?: string;
  all_day: boolean;
};

/**
 * X3.5 — the ICS calendar widget's React half.
 *
 * Events are ordered by start (the feed's order is not trusted) and clamped to
 * `max_events`. Times render in the binding's explicit zone, never the ambient
 * one, so a DST boundary cannot silently shift an event
 * (docs/DATA_WIDGET_SPEC.md §7). `all_day` events show no time.
 *
 * `mode` controls the window: `agenda` is the next N events; `day` filters to the
 * calendar day of `now`; `week` to the seven days starting on that day. `now`
 * comes from the binding so the filter is deterministic for tests and goldens.
 */
export function CalendarWidgetView({
  block,
  binding = EMPTY_BINDING
}: {
  block: CalendarWidget;
  binding?: WidgetDataBinding;
}) {
  const state = loadValue<CalendarValue>(binding, 'calendar', block.props.source.data_key);
  const timezone = binding.timezone;

  if (state.status !== 'ready' && state.status !== 'stale') {
    return <WidgetStateNotice status={state.status} reason={state.status === 'error' ? state.reason : undefined} />;
  }

  const value = state.value;
  const props = block.props;
  const ordered = [...value.events].sort((a, b) => Date.parse(a.start) - Date.parse(b.start));
  const attribution = attributionText(value, binding, 'calendar');

  let windowed: PlacedEvent[] = ordered;
  if (props.mode === 'day' || props.mode === 'week') {
    const anchor = binding.now ? Date.parse(binding.now) : Date.parse(value.fetched_at);
    const dayMs = 86_400_000;
    const span = props.mode === 'day' ? dayMs : dayMs * 7;
    const from = Number.isNaN(anchor) ? 0 : anchor;
    const to = from + span;
    windowed = ordered.filter((event) => {
      const at = Date.parse(event.start);
      return !Number.isNaN(at) && at >= from && at < to;
    });
  }
  const events = windowed.slice(0, props.max_events);

  return (
    <div className={`player-calendar player-calendar-${props.mode}`} data-calendar-mode={props.mode}>
      {state.status === 'stale' ? <StaleNotice lastGood={state.lastGood} timezone={timezone} /> : null}

      <ul className="player-calendar-events">
        {events.map((event) => (
          <li key={`${event.title}-${event.start}`} className="player-calendar-event">
            <span className="player-calendar-when mono">
              {event.all_day ? (
                <span className="player-calendar-allday">All day · {formatLocalDay(event.start, timezone)}</span>
              ) : (
                <>
                  <span className="player-calendar-day">{formatLocalDay(event.start, timezone)}</span>
                  <span className="player-calendar-time">{formatLocalTime(event.start, timezone)}</span>
                  {event.end ? <span className="player-calendar-end">–{formatLocalTime(event.end, timezone)}</span> : null}
                </>
              )}
            </span>
            <span className="player-calendar-title">{event.title}</span>
            {props.show_location && event.location ? (
              <span className="player-calendar-location mono">{event.location}</span>
            ) : null}
          </li>
        ))}
        {events.length === 0 ? <li className="player-widget-state-label">No events</li> : null}
      </ul>

      {attribution ? <AttributionLine text={attribution} url={value.attribution?.url} /> : null}
    </div>
  );
}

/** Re-exported for tests that assert the windowing key directly. */
export { localDayKey };
